import React, { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';

import { AiFormFieldReviewState, AiFormTransitionAction } from 'constants/aiProcessRuns';

import {
  reviewFormFieldsAction,
  selectIsFormActionInFlight,
  transitionFormAction,
} from 'store/aiProcessRuns';

import {
  isFieldActionable,
  resolveAvailableTransitions,
  resolveBulkEligibility,
} from '../../formReview.utils';
import {
  FORM_REVIEW_LABELS,
  FORM_TRANSITION_DEFINITIONS,
  FORM_TRANSITION_TONE,
  FormReviewTestIds,
} from '../../FormReviewCard.constants';
import FormReviewBulkSummary from '../FormReviewBulkSummary';
import FormReviewSignOffSummary from '../FormReviewSignOffSummary';
import { footerStyles } from '../FormReviewSurface.styles';

const FormReviewSurfaceControls = ({ form }) => {
  const dispatch = useDispatch();
  const formId = form.id;
  const isInFlight = useSelector(selectIsFormActionInFlight(formId));
  const [isSigningOff, setIsSigningOff] = useState(false);
  const [isBulkAgreeing, setIsBulkAgreeing] = useState(false);

  const transitionKeys = resolveAvailableTransitions(form.status);
  const canBulkAgree = isFieldActionable(form.status);

  const onTransition = useCallback(
    action => () => {
      if (action === AiFormTransitionAction.APPROVE) {
        setIsSigningOff(true);

        return;
      }

      dispatch(transitionFormAction({ formId, action }));
    },
    [dispatch, formId],
  );

  const onConfirmApprove = useCallback(() => {
    dispatch(transitionFormAction({ formId, action: AiFormTransitionAction.APPROVE }));
    setIsSigningOff(false);
  }, [dispatch, formId]);

  const onCancelSignOff = useCallback(() => {
    setIsSigningOff(false);
  }, []);

  const onBulkAgreeOpen = useCallback(() => {
    setIsBulkAgreeing(true);
  }, []);

  const onBulkAgreeCancel = useCallback(() => {
    setIsBulkAgreeing(false);
  }, []);

  const onBulkAgreeConfirm = useCallback(async () => {
    const { eligible } = resolveBulkEligibility(form);

    if (eligible.length === 0) {
      setIsBulkAgreeing(false);

      return;
    }

    const reviews = eligible.map(item => ({
      fieldKey: item.fieldKey,
      reviewState: AiFormFieldReviewState.AGREED,
    }));

    try {
      await dispatch(reviewFormFieldsAction({ formId, reviews }));
      setIsBulkAgreeing(false);
    } catch {
      // summary stays open; error toast already shown by catchAsyncActionError
    }
  }, [dispatch, form, formId]);

  if (transitionKeys.length === 0) {
    return null;
  }

  if (isSigningOff) {
    return (
      <FormReviewSignOffSummary
        form={form}
        isInFlight={isInFlight}
        onCancel={onCancelSignOff}
        onConfirm={onConfirmApprove}
      />
    );
  }

  if (isBulkAgreeing) {
    return (
      <FormReviewBulkSummary
        form={form}
        isInFlight={isInFlight}
        onCancel={onBulkAgreeCancel}
        onConfirm={onBulkAgreeConfirm}
      />
    );
  }

  return (
    <Box sx={footerStyles}>
      {isInFlight ? <CircularProgress size={14} thickness={4} /> : null}
      {canBulkAgree ? (
        <Button
          data-testid={FormReviewTestIds.BULK_AGREE_BUTTON}
          disabled={isInFlight}
          size="small"
          variant="line"
          onClick={onBulkAgreeOpen}
        >
          {FORM_REVIEW_LABELS.BULK_AGREE}
        </Button>
      ) : null}
      {transitionKeys.map(key => {
        const definition = FORM_TRANSITION_DEFINITIONS[key];
        const isAffirmative = definition.tone === FORM_TRANSITION_TONE.AFFIRMATIVE;

        return (
          <Button
            data-testid={FormReviewTestIds.TRANSITION_BUTTON}
            disabled={isInFlight}
            key={key}
            size="small"
            variant={isAffirmative ? undefined : 'line'}
            onClick={onTransition(definition.action)}
          >
            {definition.label}
          </Button>
        );
      })}
    </Box>
  );
};

FormReviewSurfaceControls.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    schemaVersionMatches: PropTypes.bool,
    fields: PropTypes.array,
    tableDiffs: PropTypes.array,
  }).isRequired,
};

export default memo(FormReviewSurfaceControls);
